import { nameOf, UNKNOWN_ACCOUNT_TYPE } from "@/lib/format";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { CreditCard, Bank, PaypalLogo, Money, Wallet } from "@phosphor-icons/react";

export const PS_TYPE_META = {
  credit_card: { label: "Credit Card", icon: CreditCard },
  checking: { label: "Checking", icon: Bank },
  savings: { label: "Savings", icon: Bank },
  prepaid: { label: "Prepaid", icon: Wallet },
  // Legacy types mapped for backward compat
  bank_account: { label: "Bank Account", icon: Bank },
  paypal: { label: "PayPal", icon: PaypalLogo },
  cash: { label: "Cash", icon: Money },
  other: { label: "Other", icon: Wallet },
};

// Mirror of backend database.py REIMBURSEMENT_METHODS — keep in sync (backend is source of truth).
export const REIMBURSEMENT_METHODS = ["Cash", "E-transfer", "Bank transfer", "Check", "Wire", "Other"];

/**
 * Unified accounts hook — fetches from /accounts-cards.
 * Returns { data: { sources: [...], types: [...] } } to stay compatible with old consumers.
 */
export function usePaymentSources(includeArchived = true) {
  return useQuery({
    queryKey: ["accounts-cards", includeArchived],
    queryFn: async () => {
      // include_deleted: tombstoned accounts come back so a historical expense
      // can still render "funded via MB WF *6046". psScopedForStudent keeps them
      // out of the pickers, exactly as it already does for archived.
      const rows = (await api.get(`/accounts-cards?include_archived=${includeArchived}&include_deleted=true`)).data;
      // Reshape to match old { sources, types } format for backward compat
      const filtered = includeArchived ? rows : rows.filter((r) => !r.archived);
      // Map unified fields to legacy fields expected by consumers
      const sources = filtered.map((r) => ({
        ...r,
        // Legacy compat fields
        nickname: r.nickname || "",
        default_currency: r.currency || "USD",
        note: r.notes || "",
      }));
      sources.sort((a, b) => (a.archived === b.archived ? 0 : a.archived ? 1 : -1) || (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0) || (a.nickname || "").localeCompare(b.nickname || ""));
      return { sources, types: ["checking", "savings", "credit_card", "prepaid", "other"] };
    },
  });
}

export function usePaymentSourceSummary(taxYear) {
  return useQuery({
    queryKey: ["accounts-cards-summary", taxYear || "all"],
    queryFn: async () => (await api.get(`/accounts-cards/summary${taxYear ? `?tax_year=${taxYear}` : ""}`)).data,
  });
}

// Resolve the real owner name for an account (for structured label initials).
// students: array of {id, full_name}, parties: {owner: {full_name, nickname}, custom: [...]}
export function psOwnerName(ps, students, parties) {
  if (!ps) return "";
  if (ps.owner_type === "student") return (students || []).find(s => s.id === ps.student_id)?.full_name || "";
  return parties?.owner?.full_name || parties?.owner?.nickname || "";
}

// Derive up-to-two initials from a full name, the one rule used everywhere.
// Initials derive from an EXPLICIT separator predicate, hand-written
// identically here and in backend/helpers.py — never from \s or str.split(),
// whose whitespace definitions differ between the two languages in BOTH
// directions (JS splits U+FEFF, Python does not; Python splits U+001C-001F,
// JS does not). One predicate, two verbatim copies, shared fixtures pinning
// them. Change BOTH or neither.
const SEP_CODES = new Set([0x85, 0xA0, 0x1680, 0x2028, 0x2029, 0x202F, 0x205F, 0x3000, 0xFEFF, 0x180E,
  0x1C, 0x1D, 0x1E, 0x1F]);
const isSep = (ch) => {
  const o = ch.codePointAt(0);
  return o <= 0x20 || SEP_CODES.has(o) || (o >= 0x2000 && o <= 0x200A);
};
// Rule (identical on both sides, counted in CODE POINTS, never UTF-16
// units): first code point of up to two words, uppercase the pair, truncate
// to two code points. Full uppercase is used deliberately — accented names
// (Élodie, Éloise) are this product's reality and their case mappings have
// been frozen in Unicode for decades. ACCEPTED RESIDUAL: a letter whose case
// mapping is ADDED in a new Unicode version (e.g. U+019B) may briefly render
// differently between app and PDF until both runtimes ship the same tables —
// cosmetic, self-healing, and no fixture-stable rule exists for it.
// Uppercase ONLY where the mapping is frozen and identical on every runtime:
// ASCII a-z, Latin-1 Supplement (à-þ), and Latin Extended-A (Ā-ſ). That
// covers every accented European name this product actually serves — éloise
// still yields É. Anything outside those ranges passes through untouched,
// because newer Unicode versions ADD case mappings (U+019B gained one) and
// Python and browsers ship different tables: uppercasing there would let the
// app and the tax PDF print different initials for the same name. Rounds 5
// and 6 of review argued ASCII-only vs full-uppercase; this is the third
// option that satisfies both. Mirror: _upper_stable in backend/helpers.py.
const upperStable = (ch) => {
  const o = ch.codePointAt(0);
  const stable = (o >= 0x61 && o <= 0x7a)
    || (o >= 0xdf && o <= 0xfe && o !== 0xf7)
    || (o >= 0x100 && o <= 0x17f)
    || (o >= 0x3b1 && o <= 0x3c9)   // Greek small letters
    || (o >= 0x430 && o <= 0x45f);  // Cyrillic small letters incl. ё and friends
  return stable ? ch.toUpperCase() : ch;
};
const nameInitials = (name) => {
  const raw = [];
  let inWord = false;
  for (const ch of (name || "")) {          // for..of iterates code points
    if (isSep(ch)) { inWord = false; continue; }
    if (!inWord) { raw.push(upperStable(ch)); inWord = true; if (raw.length >= 2) break; }
  }
  return Array.from(raw.join("")).slice(0, 2).join("");
};

// A 529 fund account's last-4: the real field, falling back to trailing
// digits embedded in a legacy stored label. This rule lived only in the
// Accounts card, so the tax PDF and the distribution dropdown silently lost
// *last4 for legacy accounts. One helper, used by every 529 label site;
// mirrored by _fund_last4 in backend/helpers.py.
// ASCII digits only, deliberately: Python's isdigit() accepts Unicode digits
// and JS \d does not, so anything wider lets the PDF and the app disagree.
export function fundLast4(fa) {
  if (fa?.account_number_last4) return fa.account_number_last4;
  // The legacy fallback accepts ONE shape: the stored label ENDS with a run of
  // four or more digits. Anything looser invents account numbers out of
  // ordinary words — "Fall 2024 529 Plan" gave *4529, then "Fall 2024 Plan"
  // gave *2024 — and this value is printed in a tax document. Walked by hand
  // rather than by regex so it reuses isSep and cannot drift from the backend
  // on whitespace or digit classes.
  const chars = Array.from(fa?.label || "");
  let k = chars.length - 1;
  while (k >= 0 && isSep(chars[k])) k--;
  const run = [];
  while (k >= 0 && chars[k] >= "0" && chars[k] <= "9") { run.unshift(chars[k]); k--; }
  return run.length >= 4 ? run.slice(-4).join("") : "";
}

// Trim with the SHARED separator predicate. JS trim() and Python strip()
// disagree on characters like U+FEFF and U+001C, which would let the app
// and the PDF print different labels for the same account.
const trimSep = (str) => {
  const chars = Array.from(str || "");
  let a = 0, b = chars.length;
  while (a < b && isSep(chars[a])) a++;
  while (b > a && isSep(chars[b - 1])) b--;
  return chars.slice(a, b).join("");
};

// Structured label: "{Initials} {Institution} *{last4}{ currency if not USD}"
// Initials come from the account owner's real name, not the nickname.
// Pass ownerName (the student's or owner's full_name) for correct initials.
// DELIBERATELY ABSENT: the account type is never part of the label (owner ruling,
// 2026-08-09). A type-bearing label ("MB Checking") reads as a real account name,
// and the type is already shown on its own line in the card. An account with no
// holder yet labels to "" on purpose — the label fills in as the fields are filled.
//
// 529 plans additionally carry the BENEFICIARY's initials at the very end
// (ZEN-4, owner rulings 2026-08-10: wanted regardless of per-beneficiary
// filtering; placement after the last-4): "MB 529 Plan CO *5678 LA". The label
// then identifies the plan on its own, anywhere it appears. Non-529 accounts
// never get this — their owner IS the person the initials already name.
//
// MIRROR WARNING: backend/helpers.py _ps_label is the same logic for PDFs and
// exports. Change BOTH or the app and the tax packet disagree about the same
// account's name (ZEN-63 divergent-duplicate class); test_ps_label_initials.py
// pins the shared fixtures.
export function psLabel(ps, ownerName, beneficiaryName) {
  if (!ps) return "";
  const initials = nameInitials(ownerName || ps.nickname || "");
  const is529 = (ps.type === "529_plan" || ps.type === "529");
  const inst = is529
    ? ("529 Plan" + (ps.state ? ` ${ps.state}` : ""))
    : (ps.institution || "");
  const l4 = ps.last4 ? `*${ps.last4}` : "";
    // Uppercased through the shared stable helper, not toUpperCase(): ISO 4217
  // codes are ASCII, but nothing validates that on the way in.
  const cur = Array.from(ps.currency || ps.default_currency || "USD").map(upperStable).join("");
  const curSuffix = cur && cur !== "USD" ? ` ${cur}` : "";
  const benInitials = is529 ? nameInitials(beneficiaryName) : "";
  const parts = [initials, inst, l4, benInitials].filter(Boolean);
  return trimSep(parts.join(" ") + curSuffix) || ps.nickname || "";
}

// "Credit Card · *1345 · USD"
export function psMeta(ps) {
  if (!ps) return "";
  // A payment method's kind is the same thing as an account's kind — this
  // table is the legacy mirror of ACCOUNT_TYPE_LABEL — so it gets the same
  // answer Maryse chose there: usually nobody ever said. (ZEN-635; found by
  // the sweep once it could see a fallback that follows a property read.)
  const t = nameOf(PS_TYPE_META, ps.type, {}).label || UNKNOWN_ACCOUNT_TYPE;
  const parts = [t];
  if (ps.last4) parts.push(`*${ps.last4}`);
  const cur = ps.currency || ps.default_currency;
  if (cur) parts.push(cur);
  return parts.join(" · ");
}

export function psById(sources, id) {
  return (sources || []).find((s) => s.id === id) || null;
}

// WHO PAID DECIDES WHICH ACCOUNTS ARE ON THE MENU. (ZEN-53)
//
// Every payer used to be shown the same list — the account owner's accounts
// plus the selected beneficiary's — so choosing "Owner" or "Student" changed
// nothing, and the one right answer for "529 Direct" was not on the menu at
// all, because 529 plans are not kept with bank accounts and cards.
//
//   Student     the beneficiary's own accounts
//   Owner       the account owner's accounts
//   529 Direct  no bank account exists to name — the form shows the plan
//               instead, read-only, and records no payment account
//   Other       unchanged: the account owner's plus this beneficiary's
//
// selectedId is ALWAYS kept on the list. An account the record already points
// at has to stay visible even when it does not fit the payer — otherwise the
// screen shows an empty box while the saved record still names an account,
// which is the screen disagreeing with the record. Pass null for selectedId to
// ask the stricter question — "does this account really belong to this payer" —
// which is what the pre-selection uses.
export function psForPayor(sources, payorType, studentId, selectedId) {
  return (sources || []).filter((s) => {
    const archiveOk = !s.archived || s.id === selectedId;
    const deletedOk = !s.deleted || s.id === selectedId;
    if (!archiveOk || !deletedOk) return false;
    if (selectedId && s.id === selectedId) return true;
    // A BENEFICIARY MAY PAY WITH THE FAMILY CARD. (ZEN-283)
    //
    // The first version of this list read "the beneficiary's own accounts,
    // nothing else", and the merge gate's reviewer showed where that lands: a
    // household whose accounts all predate the per-person model has NO account
    // marked as any beneficiary's, so "the beneficiary paid" could not be
    // recorded against anything at all. A student handing over the family card
    // is one of the most ordinary ways a student pays for anything, and an app
    // that cannot write that down is refusing to record what happened.
    //
    // What the list still keeps out is the thing the ticket was really about:
    // ONE PERSON'S PRIVATE ACCOUNT NEVER APPEARS UNDER ANOTHER PERSON'S NAME.
    // A beneficiary sees their own accounts and the household's, never a
    // sibling's; the account owner sees the household's, never a
    // beneficiary's. The server refuses exactly what this list leaves out, so
    // the screen and the rule cannot disagree.
    if (payorType === "student") {
      if (s.owner_type !== "student") return true;          // a household account
      return !!studentId && s.student_id === studentId;     // or their own
    }
    if (payorType === "owner") return s.owner_type !== "student";
    if (payorType === "529") return false;
    // "Other", and the moment before anyone has said who paid.
    return s.owner_type !== "student" || s.student_id === studentId;
  });
}

// The one account a given payer starts on. Archived, deleted and other
// people's accounts are excluded by construction — selectedId is null — so a
// holder with no default of their own gets nothing rather than somebody
// else's account.
export function psDefaultForPayor(sources, payorType, studentId) {
  if (payorType !== "student" && payorType !== "owner") return null;
  const eligible = psForPayor(sources, payorType, studentId, null);
  // THE BENEFICIARY'S OWN DEFAULT, NEVER THE HOUSEHOLD'S STANDING IN FOR IT.
  //
  // Widening the beneficiary's list to include household accounts put two
  // defaults on it — theirs and the account owner's — and simply taking the
  // first left the winner decided by alphabetical order. A beneficiary with a
  // default of their own could open the form and find the family card sitting
  // in the box instead. A person with no default of their own still gets an
  // empty box: the account owner's default is the ACCOUNT OWNER's, and
  // falling back to it is the "somebody else's account" this whole change
  // exists to stop. (Adversarial review of ZEN-283.)
  if (payorType === "student") {
    return eligible.find((s) => s.is_default
      && s.owner_type === "student" && !!studentId && s.student_id === studentId) || null;
  }
  return eligible.find((s) => s.is_default) || null;
}

// Filter payment sources to those relevant for a specific student:
// owner-level accounts (shared/family) always show; student-level accounts
// only show if they belong to the given studentId.
// selectedId: the currently-selected source id (always included even if archived).
export function psScopedForStudent(sources, studentId, selectedId) {
  return (sources || []).filter((s) => {
    const archiveOk = !s.archived || s.id === selectedId;
    // A deleted account is never offered, but stays selectable when it is what
    // the record already points at — otherwise editing an old expense would
    // silently drop its account.
    const deletedOk = !s.deleted || s.id === selectedId;
    const scopeOk = s.owner_type !== "student" || s.student_id === studentId;
    return archiveOk && deletedOk && scopeOk;
  });
}
